import { useState, FormEvent } from 'react'
import { Phone, MapPin, Instagram, Palette, ShieldCheck, Sparkles } from 'lucide-react'
import Button from '../components/common/Button'
import Reveal from '../components/common/Reveal'
import HeroScene from '../components/common/HeroScene'
import WorkImage from '../components/common/WorkImage'
import InstructorReveal from '../components/common/InstructorReveal'
import { H1, H2, H3, Body1 } from '../components/common/Typography'
import { getClientId } from '../lib/fingerprint'
import {
  submitConsultation,
  localSubmit,
  toDeviceInfo,
  type ConsultationEntry,
} from '../lib/analytics'

const HomePage = () => {
  const [submitted, setSubmitted] = useState(false)
  const [videoOk, setVideoOk] = useState(false)
  const [form, setForm] = useState({ name: '', phone: '', message: '' })

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault()
    const name = form.name.trim()
    const phone = form.phone.trim()
    if (!name || !phone) return

    // ارسال به سرور آمار؛ در دسترس نبودن سرور → ذخیرهٔ محلی برای داشبورد
    const entry: ConsultationEntry = {
      id: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
      name,
      phone,
      message: form.message.trim(),
      createdAt: Date.now(),
      device: toDeviceInfo(),
      clientId: getClientId(),
    }
    const ok = await submitConsultation(entry)
    if (!ok) localSubmit(entry)
    setSubmitted(true)
  }

  const courses = [
    {
      icon: Sparkles,
      title: 'دوره صفر تا صد ناخن',
      duration: 'از پایه تا استادی',
      description:
        'مسیر کامل حرفه‌ای؛ از اصول بهداشت و آماده‌سازی بستر تا کاشت، ترمیم و پایان‌بندی در سطح بازار کار. برای کسی که می‌خواهد از صفر به یک ناخن‌کار قابل‌اتکا تبدیل شود.',
    },
    {
      icon: Palette,
      title: 'دوره طراحی ناخن',
      duration: 'هنر روی نوک انگشتان',
      description:
        'از ترکیب‌بندی رنگ و تکنیک‌های براش تا طرح‌های امضای شخصی. این دوره برای کسی است که ناخن را بوم نقاشی می‌بیند و می‌خواهد امضای خودش را روی کار بگذارد.',
    },
  ]

  const works = [
    'https://images.unsplash.com/photo-1607773967209-6c879c220cda?w=600&h=750&fit=crop&auto=format',
    'https://images.unsplash.com/photo-1604654894610-df63bc536371?w=600&h=750&fit=crop&auto=format',
    'https://images.unsplash.com/photo-1612810806696-70456aac5e1a?w=600&h=750&fit=crop&auto=format',
    'https://images.unsplash.com/photo-1562322140-8baeececf3df?w=600&h=750&fit=crop&auto=format',
  ]

  return (
    <div>
      {/* Hero — صحنهٔ زندهٔ فعالیت ناخن‌کاری + ویدیوی واقعی وقتی موجود باشد */}
      <section id="home" className="relative min-h-screen flex items-center overflow-hidden">
        <HeroScene videoOk={videoOk} onVideoState={setVideoOk} onVideoError={() => {}} />
        <div className="absolute inset-0 bg-gradient-to-b from-black/35 via-black/20 to-black/65" />

        <div className="relative z-10 container-custom text-center pt-28 pb-24 md:pt-32 md:pb-28">
          <Reveal>
            <p className="mb-6 md:mb-7 text-sm tracking-[0.4em] uppercase text-white/80">
              الهام ابویی
            </p>
            <H1 className="!text-white !text-4xl sm:!text-6xl md:!text-8xl !leading-[1.15] text-balance mb-6 md:mb-8">
              جایی که ناخن،
              <br />
              یک اثر هنری می‌شود
            </H1>
            <p className="max-w-xl mx-auto mb-10 md:mb-12 text-white/95 leading-8 md:leading-9 text-base md:text-lg">
              من الهام ابویی هستم؛ مدرس طراحی و کاشت ناخن در یزد.              به تازه‌کارها از صفرِ صفر یاد می‌دهم تا با دستِ خودشان وارد بازار کار شوند.
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <Button variant="luxury" size="lg" href="#consultation">
                رزرو
              </Button>
              <a
                href="#portfolio"
                className="group inline-flex items-center gap-3 rounded-full border border-white/25 px-6 md:px-8 py-4 text-sm md:text-base font-medium text-white transition-all duration-300 hover:border-white/60 hover:bg-white/5"
              >
                تماشای نمونه‌کارها
                <span className="h-1.5 w-1.5 rotate-45 bg-white/70 transition-transform duration-300 group-hover:translate-x-1" />
              </a>
            </div>
          </Reveal>
        </div>
      </section>

      {/* نوار سه‌گانهٔ ارزش‌ها — همیشه افقی، حتی در موبایل */}
      <div className="border-b border-gray-200 dark:border-gray-800 bg-white/60 dark:bg-white/[0.02]">
        <div className="container-custom grid grid-cols-3 divide-x divide-x-reverse divide-gray-200 dark:divide-gray-800">
          {[['۱', 'آموزش صفر تا صد'], ['۲', 'تمرین روی مدل زنده'], ['۳', 'همراهی تا همکاری']].map(([num, label]) => (
            <div key={label} className="flex items-center justify-center gap-2 md:gap-4 py-4 md:py-6 px-1 md:px-4">
              <span className="flex h-7 w-7 md:h-10 md:w-10 shrink-0 items-center justify-center rounded-full border border-secondary-400/50 text-secondary-600 dark:text-secondary-300 font-display text-sm md:text-xl">
                {num}
              </span>
              <span className="font-display text-sm md:text-2xl text-gray-800 dark:text-gray-100 text-center leading-tight">{label}</span>
            </div>
          ))}
        </div>
      </div>

      {/* درباره — عکس واقعی مدرس در همان بخشی که نامش است */}
      <InstructorReveal
        src="/images/instructor.jpg"
        name="الهام ابویی"
        role="مدرس طراحی و کاشت ناخن"
      >
        <Body1 className="text-lg text-gray-700 leading-9 md:leading-10">
          متولد مهر ۱۳۸۳، از بچگی با قلم و رنگ بزرگ شد؛ از دفترهای نقاشی دوران کودکی
          تا چهار سال کار حرفه‌ای در دنیای ناخن. برای او این حرفه فقط یک شغل نیست؛ علاقه‌ای که
          هیچ‌وقت اجازه نداده از آن دست بکشد. الونا کلاسی است برای تازه‌کارها: جایی که از اولین
          ضربهٔ قلم‌مو شروع می‌کنید و تا استقلال کامل پیش می‌روید.
        </Body1>
        <div className="mt-10 flex flex-wrap items-center justify-center md:justify-start gap-3">
          {['آموزش صفر تا صد', 'تمرین روی مدل زنده', 'همراهی تا همکاری'].map((chip) => (
            <span
              key={chip}
              className="rounded-full border border-secondary-300/60 bg-secondary-50 dark:bg-secondary-900/30 px-5 py-2 text-sm text-secondary-700 dark:text-secondary-200"
            >
              {chip}
            </span>
          ))}
        </div>
      </InstructorReveal>

      {/* Portfolio */}
      <section id="portfolio" className="py-20 md:py-28 border-t border-gray-200 dark:border-gray-800">
        <div className="container-custom">
          <Reveal className="text-center max-w-xl mx-auto mb-12 md:mb-16">
            <p className="mb-4 text-xs tracking-[0.3em] uppercase text-secondary-600 dark:text-secondary-300">
              گالری
            </p>
            <H2 className="!leading-snug text-balance mb-6">کاری که از دست‌های من برمی‌آید</H2>
            <Body1 className="text-gray-600">هر طرح، امضایی است از سلیقه شما و دقت ما.</Body1>
          </Reveal>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-6">
            {works.map((src, i) => (
              <Reveal key={src} delay={i * 120} className={i % 2 === 1 ? 'md:mt-14' : ''}>
                <div className="group rounded-xl md:rounded-2xl overflow-hidden relative">
                  <WorkImage src={src} alt="نمونه کار" />
                  <div className="absolute inset-0 bg-black/0 group-hover:bg-black/25 transition-colors duration-500 pointer-events-none" />
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Courses */}
      <section id="courses" className="container-custom py-20 md:py-28 border-t border-gray-200 dark:border-gray-800">
        <Reveal className="max-w-xl mx-auto mb-12 md:mb-16 text-center">
          <p className="mb-4 text-xs tracking-[0.3em] uppercase text-secondary-600 dark:text-secondary-300">
            دوره‌های آموزشی
          </p>
          <H2 className="!leading-snug text-balance">دو مسیر، یک مقصد: استادی</H2>
        </Reveal>

        <div className="max-w-4xl mx-auto grid md:grid-cols-2 gap-5 md:gap-6">
          {courses.map((course, i) => {
            const Icon = course.icon
            return (
              <Reveal key={course.title} delay={i * 150}>
                <div className="group h-full border border-gray-200 dark:border-gray-800 rounded-2xl md:rounded-3xl p-6 md:p-10 bg-white/50 dark:bg-white/[0.03] transition-all duration-500 hover:border-secondary-300 hover:shadow-card dark:hover:border-secondary-700 hover:-translate-y-1">
                  <div className="flex items-center justify-between mb-6">
                    <span className="h-12 w-12 md:h-14 md:w-14 rounded-2xl bg-secondary-100 dark:bg-secondary-900/40 text-secondary-600 dark:text-secondary-300 flex items-center justify-center">
                      <Icon className="h-5 w-5 md:h-6 md:w-6" />
                    </span>
                    <span className="font-display text-5xl md:text-6xl text-gray-200 dark:text-gray-800 select-none leading-none">
                      {String(i + 1).padStart(2, '۰')}
                    </span>
                  </div>
                  <H3 className="!text-2xl md:!text-3xl mb-2">{course.title}</H3>
                  <p className="text-sm tracking-widest text-secondary-500 dark:text-secondary-400 mb-4 md:mb-5">
                    {course.duration}
                  </p>
                  <Body1 className="text-gray-600 leading-8">{course.description}</Body1>
                </div>
              </Reveal>
            )
          })}
        </div>
      </section>

      {/* Consultation */}
      <section id="consultation" className="relative py-24 md:py-32 overflow-hidden border-t border-gray-200 dark:border-gray-800">
        <div className="absolute left-1/2 top-0 -translate-x-1/2 -translate-y-1/3 h-[28rem] md:h-[36rem] w-[28rem] md:w-[36rem] rounded-full bg-secondary-200/25 dark:bg-secondary-800/10 blur-3xl pointer-events-none" />

        <div className="relative container-custom">
          <Reveal className="text-center max-w-lg mx-auto mb-12 md:mb-14">
            <p className="mb-4 text-xs tracking-[0.3em] uppercase text-secondary-600 dark:text-secondary-300">
              مشاوره
            </p>
            <H2 className="!leading-snug text-balance mb-6">شروع هر کار خوب، یک گفت‌وگوست</H2>
            <Body1 className="text-gray-600">
              فرم را پر کنید؛ خودم تماس می‌گیرم. بدون عجله، بدون تعارف — فقط درباره هدف شما و
              بهترین مسیر برای رسیدن به آن.
            </Body1>
          </Reveal>

          <Reveal delay={150}>
            <div className="max-w-xl mx-auto rounded-2xl md:rounded-[2rem] border border-gray-200 dark:border-gray-800 bg-white/70 dark:bg-white/[0.03] backdrop-blur-sm shadow-card p-6 md:p-12">
              {submitted ? (
                <div className="text-center py-10">
                  <span className="mx-auto mb-6 flex h-16 w-16 items-center justify-center rounded-full bg-secondary-100 dark:bg-secondary-900/40 text-secondary-600 dark:text-secondary-300">
                    <ShieldCheck className="h-8 w-8" />
                  </span>
                  <H3 className="!text-2xl mb-3">درخواست شما ثبت شد</H3>
                  <Body1 className="text-gray-600">
                    به‌زودی از شماره {form.phone} با شما تماس می‌گیرم.
                  </Body1>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-8">
                  <div className="grid sm:grid-cols-2 gap-8">
                    <label className="block">
                      <span className="mb-3 block text-xs font-medium tracking-widest text-gray-500">
                        نام و نام‌خانوادگی
                      </span>
                      <input
                        type="text"
                        required
                        value={form.name}
                        onChange={(e) => setForm({ ...form, name: e.target.value })}
                        className="luxury-input"
                      />
                    </label>
                    <label className="block">
                      <span className="mb-3 block text-xs font-medium tracking-widest text-gray-500">
                        شماره تماس
                      </span>
                      <input
                        type="tel"
                        required
                        dir="ltr"
                        value={form.phone}
                        onChange={(e) => setForm({ ...form, phone: e.target.value })}
                        className="luxury-input text-left"
                      />
                    </label>
                  </div>

                  <label className="block">
                    <span className="mb-3 block text-xs font-medium tracking-widest text-gray-500">
                      درباره هدف شما
                    </span>
                    <textarea
                      rows={4}
                      value={form.message}
                      onChange={(e) => setForm({ ...form, message: e.target.value })}
                      className="luxury-input resize-none"
                    />
                  </label>

                  <Button type="submit" variant="luxury" size="lg" fullWidth>
                    ثبت درخواست مشاوره
                  </Button>

                  <div className="flex flex-wrap items-center justify-center gap-x-8 gap-y-3 text-sm text-gray-500 border-t border-gray-200 dark:border-gray-800 pt-6">
                    <a href="tel:09139079448" className="flex items-center gap-2 hover:text-primary-600 transition-colors">
                      <Phone className="h-4 w-4" /> ۰۹۱۳۹۰۷۹۴۴۸
                    </a>
                    <span className="flex items-center gap-2">
                      <MapPin className="h-4 w-4" /> یزد، مهریز
                    </span>
                    <a href="#" className="flex items-center gap-2 hover:text-primary-600 transition-colors">
                      <Instagram className="h-4 w-4" /> اینستاگرام
                    </a>
                  </div>
                </form>
              )}
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  )
}

export default HomePage
